import Button from "@components/ui/buttons/Button";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useEffect, useState } from "react";
import DataGrid from "@components/ui/tables/DataGrid";
import { transformPartnersToTable } from "@components/ui/tables/transformToTables";
import { useProjectStore } from "@store/projectStore";
import { CreatePartnerModal } from "@components/modals/CreatePartnerModal";
import { ModifyPartnerModal } from "@components/modals/ModifyPartnerModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";
import { ChargeArtisanTable } from "@components/ui/blocks/ChargeArtisanTable";
import { useDataStore } from "@store/user_org";

const mainTabs = [
  { value: "list", label: "Artisans" },
  { value: "charge", label: "Charge artisan" },
];

const typeTabs = [
  { value: "all", label: "Tous" },
  { value: "architecte", label: "Architecte" },
  { value: "bureau_d_etudes", label: "Bureau d'études" },
  { value: "electricite", label: "Électricité" },
  { value: "plomberie", label: "Plomberie" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "charpente", label: "Charpente" },
  { value: "gros_oeuvre", label: "Gros œuvre" },
  { value: "couverture", label: "Couverture" },
  { value: "enduit", label: "Enduit" },
  { value: "cloisonnement", label: "Cloisonnement" },
  { value: "carrelage", label: "Carrelage" },
  { value: "peinture", label: "Peinture" },
  { value: "parquet", label: "Parquet" },
];

export default function PartnersScreen() {
  const partners = useProjectStore((state) => state.partners);
  const removePartners = useProjectStore((state) => state.removePartners);
  const orgData = useDataStore((state) => state.orgData);
  const [table, setTable] = useState<any>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [modifyPartner, setModifyPartner] = useState<PartnerInfos | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [activeMainTab, setActiveMainTab] = useState<string>("list");

  useEffect(() => {
    const table = transformPartnersToTable(partners, activeTab);
    setTable(table);
  }, [partners, activeTab]);

  const handleRowClick = (row: any) => {
    const partner = partners.find((p) => p.id === row.id);
    if (partner) {
      setModifyPartner(partner);
    }
  };

  const handleDeleteSelected = () => {
    removePartners(selectedIds);
    showNotification({
      type: "success",
      message: `${selectedIds.length} intervenant(s) supprimé(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="flex flex-col  overflow-auto max-h-screen">
      {createModalOpen && (
        <CreatePartnerModal onClose={() => setCreateModalOpen(false)} />
      )}
      {modifyPartner && (
        <ModifyPartnerModal
          onClose={() => setModifyPartner(null)}
          initialPartner={modifyPartner}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} intervenant(s) ?`}
          message="Cette action est irréversible. Tous les intervenants sélectionnés seront supprimés définitivement."
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
        />
      )}
      <div className="flex flex-col h-fit pb-96 mt-10 ml-10">
        <div className="flex flex-col w-full max-w-1k gap-4">
          <div className="flex flex-row w-full justify-between">
            <div className="flex flex-col w-400">
              <Text1>Artisans</Text1>
              <Text2>Retrouvez tous vos artisans ici.</Text2>
              <Text3>
                Les intervenants sont les entreprises et contacts qui
                participent à vos projets.
              </Text3>
            </div>
            {activeMainTab === "list" && (
              <div className="flex flex-row gap-2 items-start">
                {selectedIds.length > 0 && (
                  <SecondaryButton
                    danger={true}
                    startIcon={
                      <Icon1 name="delete" size={22} color={colors.red_500} />
                    }
                    label={`Supprimer la sélection (${selectedIds.length})`}
                    variant="large"
                    onClick={() => setShowDeleteConfirm(true)}
                  />
                )}
                <Button
                  label="Nouvel intervenant"
                  onClick={() => setCreateModalOpen(true)}
                />
              </div>
            )}
          </div>

          {/* Main tabs */}
          <div className="flex flex-row gap-2">
            {mainTabs.map((t) => (
              <button
                key={t.value}
                onClick={() => setActiveMainTab(t.value)}
                className={`px-4 py-2 rounded-xl text-h3 font-medium border transition-all ${
                  activeMainTab === t.value
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeMainTab === "charge" && orgData?.id && (
            <ChargeArtisanTable orgId={orgData.id} />
          )}

          {activeMainTab === "list" && (
            <div className="flex flex-col h-fit w-1k mt-4 gap-4">
              <div className="max-h-500">
                {table && (
                  <DataGrid
                    selectable={true}
                    table={table}
                    hide_lines={true}
                    tabs={typeTabs}
                    activeTab={activeTab}
                    onTabChange={(tab) => setActiveTab(tab)}
                    onClick={handleRowClick}
                    handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
